import { ETextStyleVariant, GeneralMapPinOutlineIcon, Text } from '@outdoorsyco/bonfire';
import { clsx } from 'clsx';
import React from 'react';

import { ILocationSearchAutocompleteListOption } from '@/hooks/useLocationSearchAutocompleteOptions';

import { ELocationSearchAutocompleteListVariant } from './LocationSearchAutocompleteList';

interface LocationSearchAutocompleteListItemProps {
  option: ILocationSearchAutocompleteListOption;
  optionIndex: number;
  optionProps?: React.HTMLAttributes<HTMLDivElement> & { key?: string | number };
  totalOptions: number;
  variant: ELocationSearchAutocompleteListVariant;
  defaultOptionPerColumnMax?: number;
  isInLastGroup: boolean;
}

export const LocationSearchAutocompleteListItem: React.FC<
  LocationSearchAutocompleteListItemProps
> = ({
  option,
  optionIndex,
  optionProps,
  totalOptions,
  variant,
  defaultOptionPerColumnMax,
  isInLastGroup,
}) => {
  const optionPerColumnMax = defaultOptionPerColumnMax ?? 0;

  const { key, ...props } = optionProps || {};

  const hideBottomBorder = React.useMemo(() => {
    switch (variant) {
      case ELocationSearchAutocompleteListVariant.Horizontal:
        if (totalOptions > optionPerColumnMax) {
          const isLastItemInFirstColumn = optionIndex === Math.min(2, totalOptions - 1);
          const isLastItem = optionIndex === totalOptions - 1;
          const columnsNotFullyFilled = totalOptions < optionPerColumnMax * 2;

          return isLastItemInFirstColumn || (isLastItem && !columnsNotFullyFilled);
        }
        return false;
      case ELocationSearchAutocompleteListVariant.Vertical:
        return isInLastGroup && optionIndex === totalOptions - 1;
      default:
        return false;
    }
  }, [variant, totalOptions, optionPerColumnMax, optionIndex, isInLastGroup]);

  return (
    <div
      className={clsx('flex flex-col py-3 truncate border-b border-gray-200 cursor-pointer gap-4', {
        'border-none': hideBottomBorder,
      })}
      key={key}>
      <div
        className="flex items-center w-full border-4 border-transparent gap-4 rounded-2xl hover:bg-gray-10 hover:border-gray-10"
        {...props}>
        <div className="flex items-center justify-center bg-gray-10 w-14 h-14 rounded-2xl shrink-0">
          {option.icon ? (
            <option.icon />
          ) : (
            <GeneralMapPinOutlineIcon className="w-6 h-6 text-gray-900" />
          )}
        </div>
        <div className="flex flex-col flex-1 min-w-0">
          <Text
            key={option.value?.mapbox_id || `${option.label}-${optionIndex}`}
            variant={ETextStyleVariant.MediumRegular}
            className="text-left text-black truncate">
            {option.label}
          </Text>
          {!!option.sublabel && (
            <Text
              variant={ETextStyleVariant.LegalRegular}
              className="text-left truncate text-gray-50">
              {option.sublabel}
            </Text>
          )}
        </div>
      </div>
    </div>
  );
};
